import React from 'react'
import { EyeIcon, InboxIcon } from '@heroicons/react/24/outline'
import type { OfimaPedidoListItem } from '../../../types/ofimaPedidos'
import { facturaTiposVisibles, formatOfimaDate, ofimaDisplayValue } from '../../../utils/ofimaPedidos'
import OfimaTipoBadge from './OfimaTipoBadge'

interface PedidosOfimaTableProps {
  pedidos: OfimaPedidoListItem[]
  loadedCount: number
  isLoading: boolean
  hasClientFilters: boolean
  onView: (orden: string) => void
}

const HEADERS = [
  { label: 'Pedido', align: 'text-left' },
  { label: 'Creación Ofima', align: 'text-left' },
  { label: 'NIT', align: 'text-left' },
  { label: 'Remisiones', align: 'text-center' },
  { label: 'Facturas', align: 'text-left' },
  { label: 'Notas crédito', align: 'text-center' },
  { label: 'Acciones', align: 'text-right' }
]

function CountCell({
  value,
  multiple
}: {
  value: number
  multiple: boolean
}) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      <span className="inline-flex min-w-[1.75rem] items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-sm font-semibold text-gray-900">
        {value}
      </span>
      {multiple && (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          Varias
        </span>
      )}
    </div>
  )
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <tr key={index} aria-hidden="true">
          <td colSpan={HEADERS.length} className="px-4 py-3">
            <div className="h-4 animate-pulse rounded bg-gray-100" />
          </td>
        </tr>
      ))}
    </>
  )
}

const PedidosOfimaTable: React.FC<PedidosOfimaTableProps> = ({
  pedidos,
  loadedCount,
  isLoading,
  hasClientFilters,
  onView
}) => {
  const showSkeleton = isLoading && pedidos.length === 0

  if (!isLoading && pedidos.length === 0) {
    return (
      <div className="px-6 py-14 text-center">
        <InboxIcon className="mx-auto h-10 w-10 text-gray-300" aria-hidden="true" />
        <h3 className="mt-3 text-base font-medium text-gray-900">No hay pedidos</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
          {hasClientFilters && loadedCount > 0
            ? 'Ningún pedido de esta página coincide con los filtros. Prueba a quitar alguno.'
            : 'No se encontraron pedidos Ofima para esta consulta.'}
        </p>
      </div>
    )
  }

  return (
    <div className={isLoading && !showSkeleton ? 'opacity-60' : ''} aria-busy={isLoading}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {HEADERS.map((column) => (
                <th
                  key={column.label}
                  className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${column.align}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {showSkeleton ? (
              <SkeletonRows />
            ) : (
              pedidos.map((pedido) => {
                const tipos = facturaTiposVisibles(pedido)
                return (
                  <tr
                    key={pedido.orden}
                    className="cursor-pointer hover:bg-blue-50/60"
                    onClick={() => onView(pedido.orden)}
                  >
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-semibold text-blue-900">
                      {pedido.orden}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {ofimaDisplayValue(formatOfimaDate(pedido.fechaCreacion))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                      {ofimaDisplayValue(pedido.nit)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <CountCell value={pedido.cantidadRemisiones} multiple={pedido.multiplesRemisiones} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <CountCell value={pedido.cantidadFacturas} multiple={pedido.multiplesFacturas} />
                        {tipos.map((tipo) => (
                          <OfimaTipoBadge key={tipo} tipo={tipo} />
                        ))}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <CountCell value={pedido.cantidadNotasCredito} multiple={pedido.multiplesNotasCredito} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          onView(pedido.orden)
                        }}
                        className="text-green-500 hover:text-green-700"
                        title="Ver"
                        aria-label={`Ver pedido ${pedido.orden}`}
                      >
                        <EyeIcon className="h-6 w-6" />
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PedidosOfimaTable
